import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBox from "@mui/icons-material/IndeterminateCheckBox";
import type {
  AutocompleteChangeReason,
  AutocompleteCloseReason,
  AutocompleteFreeSoloValueMapping,
  AutocompleteGetTagProps,
  AutocompleteInputChangeReason,
  AutocompleteOwnerState,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteSlots,
  ChipProps,
  CreateFilterOptionsConfig,
  FilterOptionsState,
  InputLabelProps,
  PaperProps,
  PopperProps,
  TooltipProps,
  AutocompleteValue as Value,
} from "@mui/material";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
  createFilterOptions,
  mergeSlotProps,
} from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { isPlainObject } from "lodash";
import type { MouseEvent, MouseEventHandler, SyntheticEvent } from "react";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

import type { TextFieldProps } from "@citrine/atoms/text-field";
import { TextField } from "@citrine/atoms/text-field";
import { getTextSize } from "@citrine/utils/text";
import type { Off, On, Paths, Toggle } from "@citrine/utils/types";

import * as sx from "./combobox.sx";
import { useFormConfiguration } from "./form";

const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const UNCHECKED_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const INDETERMINATE_ICON = <IndeterminateCheckBox fontSize="small" />;

function ElevatedPaper(props: PaperProps) {
  return <Paper {...props} elevation={8} />;
}

/**
 * this shape allows for any kind of custom object to be defined/selected
 * however, if the object type is not a string, it must be paired with a label
 */
export type ComboboxOption<ValueType = string> = [ValueType] extends [string]
  ? // if ValueType is a string, label is optional
      | ValueType
      | {
          value: ValueType;
          group?: string;
          label?: string;
          addSeparator?: boolean;
        }
  : {
      value: ValueType;
      group?: string;
      label: string;
      addSeparator?: boolean;
    };

export type ComboboxOptionObject<ValueType = string> = Exclude<
  ComboboxOption<ValueType>,
  string
>;

export interface FreeSoloOption {
  type: "freeSoloValue";
  value: string;
}

export function isFreeSoloOption(option?: any): option is FreeSoloOption {
  return Boolean(
    option && typeof option === "object" && option.type === "freeSoloValue",
  );
}

type DisableClearableProps<DisableClearable extends Toggle> =
  undefined extends DisableClearable
    ? { disableClearable?: DisableClearable }
    : { disableClearable: DisableClearable };

type FreeSoloProps<FreeSolo extends Toggle, ValueType> = FreeSolo extends On
  ? {
      freeSolo: On;
      filterOptions?: undefined;
      onFreeSolo?: (value: string) => Promise<ValueType> | ValueType;
    }
  : {
      freeSolo?: Off;
      onFreeSolo?: undefined;
    };

export type RenderOption<T> = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: T | FreeSoloOption,
  state: AutocompleteRenderOptionState,
) => React.ReactNode;

export type ComboboxCheckListProps<ValueType> =
  | {
      checklist: true;
      renderOption?: undefined;
    }
  | {
      checklist?: false;
      renderOption?: RenderOption<ValueType>;
    };

type MultipleProps<
  Shape extends FormikValues,
  ValueType,
  Multiple extends Toggle,
  DisableClearable extends Toggle,
> = Multiple extends On
  ? {
      multiple: On;
      name: Paths<Shape, ValueType[]>;
      // this is for when you want to render the tags outside of the autocomplete
      noTags?: boolean;
    } & ComboboxCheckListProps<ValueType>
  : {
      multiple?: Off;
      name: Paths<
        Shape,
        DisableClearable extends On ? ValueType : ValueType | undefined
      >;
      noTags?: undefined;
      checklist?: undefined;
    };

type TextFieldRootProps = "autoFocus" | "label" | "placeholder" | "required";
type ComboboxImplProps<
  Shape extends FormikValues,
  ValueType,
  Multiple extends Toggle,
  DisableClearable extends Toggle,
  FreeSolo extends Toggle,
> = Omit<
  AutocompleteProps<ValueType, Multiple, DisableClearable, FreeSolo>,
  "options" | "renderInput" | "size" | "slotProps" // doesn't support small size, needs styling updates
> &
  DisableClearableProps<DisableClearable> &
  FreeSoloProps<FreeSolo, ValueType> &
  MultipleProps<Shape, ValueType, Multiple, DisableClearable> &
  Pick<TextFieldProps, TextFieldRootProps> & {
    disableCloseOnSelect?: Multiple extends true ? true : boolean;
    filterOptionsConfig?: CreateFilterOptionsConfig<ValueType>;
    options: ComboboxOption<ValueType>[];
    selectAll?: boolean;
    slotProps?: NonNullable<
      AutocompleteProps<
        ValueType,
        Multiple,
        DisableClearable,
        FreeSolo
      >["slotProps"]
    > & {
      input?: NonNullable<TextFieldProps["slotProps"]>["input"];
    };
    tooltipPlacement?: TooltipProps["placement"];
  };

export interface ComboboxRef<
  ValueType,
  Multiple extends Toggle,
  DisableClearable extends Toggle,
  FreeSolo extends Toggle,
> {
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  clearValue: (shouldValidate?: boolean) => void;
  setValue: (
    value: Value<ValueType, Multiple, DisableClearable, FreeSolo>,
    shouldValidate?: boolean,
  ) => void;
}

export function getValue<ValueType>(option: ComboboxOption<ValueType>) {
  return (typeof option === "string" ? option : option.value) as ValueType;
}

export function getLabel<ValueType>(option: ComboboxOption<ValueType>) {
  return (
    typeof option === "string" ? option : (option.label ?? option.value)
  ) as string;
}

function nullRender() {
  return null;
}

function preventDefault(e: MouseEvent) {
  e.preventDefault();
}

const SELECT_ALL_SYMBOL = Symbol("Select All");

const EMPTY_ARRAY: any[] = [];

// when onInputChange is fired for a multiple combobox,
// do not reset the text input for these reasons
const PRESERVE_INPUT_REASONS: AutocompleteInputChangeReason[] = [
  "removeOption",
  "reset",
  "selectOption",
];

const COMBOBOX_SLOTS = {
  paper: ElevatedPaper,
  popper: sx.AlignedPopper,
} as const satisfies Partial<AutocompleteSlots>;

const ALL_OPTIONS_DISABLED = () => true;

function ComboboxImpl<
  Shape extends FormikValues,
  ValueType,
  Multiple extends Toggle,
  DisableClearable extends Toggle,
  FreeSolo extends Toggle,
>(
  {
    autoFocus,
    autoHighlight,
    checklist,
    disabled: disabledProp,
    disableCloseOnSelect,
    filterOptionsConfig,
    freeSolo: freeSoloProp,
    getOptionKey: getOptionKeyProp,
    groupBy: groupByProp,
    label,
    name,
    onClose: onCloseProp,
    onFreeSolo,
    onKeyDown: onKeyDownProp,
    onOpen: onOpenProp,
    options,
    multiple,
    noTags,
    placeholder,
    required,
    selectAll,
    slotProps,
    tooltipPlacement,
    onBlur,
    onChange,
    renderOption: renderOptionProp,
    ...props
  }: ComboboxImplProps<Shape, ValueType, Multiple, DisableClearable, FreeSolo>,
  ref: React.ForwardedRef<
    ComboboxRef<ValueType, Multiple, DisableClearable, FreeSolo>
  >,
) {
  type InternalValueType = Value<
    ValueType,
    Multiple,
    DisableClearable,
    FreeSolo
  >;
  const { formatMessage } = useIntl();
  const { readonly } = useFormConfiguration();

  const freeSolo = readonly ? false : freeSoloProp;

  /**
   * AutoComplete uses `label` as the option key by default, labels might not be
   * unique though if data hasn't been organized well, for example:
   * {
   *   "name": "General Properties~Density",
   *   "units": "gram / centimeter ** 3"
   * } and
   * {
   *   "name": "General Properties~Density (g/cm^3)",
   *   "units": "dimensionless"
   * }
   * will both have the same label `General Properties: Density (g/cm^3)` once
   * unit formatting has occurred.
   */
  const getOptionKey = useCallback(
    (option: ValueType | AutocompleteFreeSoloValueMapping<FreeSolo>) =>
      typeof option === "symbol"
        ? option.toString()
        : (getOptionKeyProp?.(option) ?? option),
    [getOptionKeyProp],
  );

  const filter = useMemo(
    () => createFilterOptions(filterOptionsConfig),
    [filterOptionsConfig],
  );

  const filterOptions = useCallback<
    NonNullable<
      AutocompleteProps<
        ValueType | FreeSoloOption,
        Multiple,
        DisableClearable,
        FreeSolo
      >["filterOptions"]
    >
  >(
    (opts, params) => {
      const filtered: (ValueType | FreeSoloOption)[] = (
        filter as (
          options: ValueType[],
          state: FilterOptionsState<ValueType>,
        ) => ValueType[]
      )(opts as ValueType[], params);

      // Suggest the creation of a new value if the inputValue does not
      // match the label of any existing options
      if (
        freeSolo &&
        params.inputValue !== "" &&
        !optionsRef.current.find(
          (option) =>
            getLabel(option).localeCompare(params.inputValue, undefined, {
              sensitivity: "base",
            }) === 0,
        )
      ) {
        filtered.push({
          type: "freeSoloValue",
          value: params.inputValue,
        });
      }

      return filtered;
    },
    [filter, freeSolo],
  );

  const [field, meta, helpers] = useField<InternalValueType | undefined>(name);
  const { isSubmitting } = useFormikContext();
  // this is the typed value, not the current selection
  const [inputValue, setInputValue] = useState<string>("");

  const [isUpdating, setIsUpdating] = useState(false);
  const disabled = Boolean(disabledProp || isSubmitting || isUpdating);

  // Stop the Backspace key from deselecting values when the input field is blank
  // and tags are disabled.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace" && !inputValue && noTags) {
        e.stopPropagation();
      }
      onKeyDownProp?.(e);
    },
    [inputValue, noTags, onKeyDownProp],
  );

  // Disabling Select All if freeSolo is enabled for now, as we don't have any
  // use cases for both settings and it will likely require revisiting the
  // Select All logic.
  const selectAllEnabled = Boolean(
    selectAll ?? (checklist && multiple && !freeSolo && options.length),
  );

  const providedValues = useMemo(() => options.map(getValue), [options]);
  const optionsLookup = useMemo(() => {
    const optionObjects: ComboboxOptionObject<ValueType>[] = options.map(
      (option) =>
        isPlainObject(option)
          ? (option as ComboboxOptionObject<ValueType>)
          : ({
              value: option as string,
            } as ComboboxOptionObject<ValueType>),
    );
    return new Map(optionObjects.map((option) => [option.value, option]));
  }, [options]);

  // If using both a checklist and multiselect mode, add a Select All option.
  // The SELECT_ALL_SYMBOL is not exposed to the user, it gets formatted by
  // the `renderCheckboxOption` function.
  const values = useMemo(
    () =>
      selectAllEnabled
        ? [SELECT_ALL_SYMBOL as unknown as ValueType, ...providedValues]
        : providedValues,
    [selectAllEnabled, providedValues],
  );

  const allOptionsSelected = selectAllEnabled
    ? (field.value as ValueType[] | undefined)?.length === providedValues.length
    : false;

  // use ref here because we don't know if the consumer has memoized the options
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const getOptionLabel = useCallback(
    // MUI Autocomplete uses `getOptionLabel` to do matching when typing in the
    // TextField - we don't want the Select All option to come up when the user
    // is searching, so we don't want to handle the SELECT_ALL_SYMBOL here.
    //
    // `getOptionLabel` is called both when rendering the dropdown options and
    // when rendering the value in the input field when the Combobox is closed.
    // As a result, a free solo value may come through as a FreeSoloOption or
    // a string.
    (value: ValueType | FreeSoloOption) => {
      if (isFreeSoloOption(value)) {
        return value.value;
      }
      const match = optionsRef.current.find(
        (option) => getValue(option) === value,
      ) as ComboboxOption<ValueType>;

      return match ? getLabel(match) : freeSolo ? (value as string) : "";
    },
    [freeSolo],
  );

  // use ref here to avoid infinite render loop
  const setFormValue = useRef(helpers.setValue);
  setFormValue.current = helpers.setValue;

  const setValue = useCallback(
    (value: InternalValueType | undefined, shouldValidate?: boolean) => {
      if (!Array.isArray(value)) {
        setInputValue(
          value ? getOptionLabel(value as ValueType | FreeSoloOption) : "",
        );
      }
      setFormValue.current?.(value, shouldValidate);
    },
    [getOptionLabel],
  );

  const renderTags = useCallback(
    (value: ValueType[], getTagProps: AutocompleteGetTagProps) =>
      value.map((option: ValueType, index: number) => (
        <sx.Tag
          data-testid="autocomplete-chip"
          color={props.color}
          {...getTagProps({ index })}
          {...((slotProps?.chip ?? {}) as ChipProps)}
          key={`autocomplete-chip-${index}`}
          size="small"
          label={getOptionLabel(option)}
          disabled={disabled}
        />
      )),
    [disabled, getOptionLabel, props.color, slotProps?.chip],
  );

  const renderCheckboxOption = useCallback(
    (
      renderOptionProps: React.HTMLAttributes<HTMLLIElement>,
      option: ValueType | symbol,
      { selected }: AutocompleteRenderOptionState,
    ) => {
      const checked =
        option === SELECT_ALL_SYMBOL ? allOptionsSelected : selected;
      const indeterminate =
        !checked && option === SELECT_ALL_SYMBOL
          ? Boolean((field.value as ValueType[] | undefined)?.length)
          : false;
      const checkboxLabel =
        option === SELECT_ALL_SYMBOL
          ? allOptionsSelected
            ? formatMessage({ id: "components.combobox.deselect-all" })
            : formatMessage({ id: "components.combobox.select-all" })
          : getOptionLabel(option as ValueType);

      return (
        // MaterialUI puts the FormControlLabel `label` and `control` props
        // inside a <label> element. If this <label> element is clicked, it
        // results in the dropdown closing, even if `disableCloseOnSelect` is
        // true.
        //
        // The closing of the Combobox dropdown appears to be caused by the
        // TextField being blurred. This can still occur when clicking the
        // `label` and `control` elements that we provide - to resolve this we
        // add a `preventDefault` click handler to the form control.
        <li {...renderOptionProps}>
          <sx.CheckboxLabel
            onClick={preventDefault}
            label={checkboxLabel}
            control={
              <Checkbox
                color={props.color}
                data-testid="autocomplete-checkbox"
                icon={UNCHECKED_ICON}
                checkedIcon={CHECKED_ICON}
                indeterminateIcon={INDETERMINATE_ICON}
                disabled={disabled}
                checked={checked}
                indeterminate={indeterminate}
              />
            }
          />
        </li>
      );
    },
    [
      allOptionsSelected,
      disabled,
      field.value,
      formatMessage,
      getOptionLabel,
      props.color,
    ],
  );

  const renderOption = useCallback(
    (
      renderOptionProps: React.HTMLAttributes<HTMLLIElement> & {
        key: any;
      },
      option: ValueType | symbol,
      renderOptionState: AutocompleteRenderOptionState,
      ownerState: AutocompleteOwnerState<
        ValueType,
        Multiple,
        DisableClearable,
        FreeSolo,
        "div"
      >,
    ) => {
      const renderFn = checklist
        ? renderCheckboxOption
        : (renderOptionProp ??
          ((
            optionProps: React.HTMLAttributes<HTMLLIElement>,
            optionValue: ValueType,
          ) => <li {...optionProps}>{getOptionLabel(optionValue)}</li>));
      const optionObject =
        option !== SELECT_ALL_SYMBOL
          ? optionsLookup.get(option as ValueType)
          : undefined;

      // Return array instead of fragment to avoid unique key warnings - we don't
      // want to add another layer of children with the fragment
      return [
        renderFn(
          renderOptionProps,
          option as ValueType,
          renderOptionState,
          ownerState,
        ),
        optionObject?.addSeparator ? (
          <hr key={`hr:${String(optionObject.value)}`} />
        ) : null,
      ];
    },
    [
      checklist,
      getOptionLabel,
      optionsLookup,
      renderCheckboxOption,
      renderOptionProp,
    ],
  );

  useImperativeHandle(ref, () => ({
    clearValue(shouldValidate) {
      setValue(undefined, shouldValidate);
    },
    setInputValue,
    setValue,
  }));

  // watch the formik value for external changes
  useLayoutEffect(() => {
    setValue(field.value);
  }, [field.value, setValue]);

  const onBlurCallback = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      field.onBlur(e);
      // For some reason, field.onBlur doesn't mark the field as touched
      helpers.setTouched(true);
      onBlur?.(e);
    },
    [field, helpers, onBlur],
  );

  const onChangeCallback = useCallback(
    (
      e: React.ChangeEvent,
      value: InternalValueType | FreeSoloOption,
      reason: AutocompleteChangeReason,
    ) => {
      if (
        selectAllEnabled &&
        (value as unknown[]).includes(SELECT_ALL_SYMBOL)
      ) {
        if (allOptionsSelected) {
          helpers.setValue([] as unknown as InternalValueType);
          onChange?.(e, [] as unknown as InternalValueType, reason);
        } else {
          helpers.setValue([...providedValues] as unknown as InternalValueType);
          onChange?.(e, providedValues as InternalValueType, reason);
        }
      } else {
        // force value into an array for easier handling
        const valueArray = multiple
          ? [...(value as InternalValueType[])]
          : ([value] as InternalValueType[]);

        // wrap handler logic for sync or async use
        function handleChange() {
          // unwrap value array
          const valueItem = (
            multiple ? valueArray : valueArray[0]
          ) as InternalValueType;

          setValue(valueItem);
          onChange?.(e, valueItem, reason);
        }

        // find free solo value from value array
        const freeSoloIndex = valueArray.findIndex((valueItem) =>
          isFreeSoloOption(valueItem),
        );

        if (freeSoloIndex >= 0) {
          // Clear input text if creating free solo value
          setInputValue("");
          const freeSoloItem = valueArray[freeSoloIndex] as FreeSoloOption;
          // convert inputValue via onFreeSolo callback
          const computedValue = (onFreeSolo?.(freeSoloItem.value) ??
            freeSoloItem.value) as InternalValueType;

          if (computedValue instanceof Promise) {
            // update value so that it can be rendered while waiting for computedValue to resolve
            setValue(value as InternalValueType);
            setIsUpdating(true);
            setDropdownOpen(false);

            // if callback value is a Promise, handle it async
            computedValue
              .then((resolvedValue) => {
                valueArray[freeSoloIndex] = resolvedValue;
                handleChange();
              })
              .finally(() => {
                setIsUpdating(false);
              });
            return;
          } else {
            // otherwise, update it in the list synchronously
            valueArray[freeSoloIndex] = computedValue;
          }
        }

        handleChange();
      }
    },
    [
      allOptionsSelected,
      helpers,
      multiple,
      providedValues,
      selectAllEnabled,
      setValue,
      onChange,
      onFreeSolo,
    ],
  );

  const onInputChange = useCallback(
    (
      _e: React.ChangeEvent,
      v: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      // Prevents the text field from being reset when filtering and doing multi-select
      if (multiple && PRESERVE_INPUT_REASONS.includes(reason)) {
        return;
      }

      setInputValue(v);
    },
    [multiple],
  );

  /**
   * Control `dropdownOpen` and `tooltipOpen` so that we can hide the Tooltip when
   * the dropdown is open. Otherwise the Tooltip renders on top of the dropdown.
   **/
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const onClose = useCallback<
    (
      event: SyntheticEvent<Element, Event>,
      reason: AutocompleteCloseReason,
    ) => void
  >(
    (event, reason) => {
      onCloseProp?.(event, reason);
      setDropdownOpen(false);
    },
    [onCloseProp, setDropdownOpen],
  );
  const onOpen = useCallback<(event: React.SyntheticEvent) => void>(
    (e) => {
      onOpenProp?.(e);
      setDropdownOpen(true);
    },
    [onOpenProp, setDropdownOpen],
  );

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const onMouseEnter = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    setTooltipOpen(true);
  }, []);

  const onMouseLeave = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    setTooltipOpen(false);
  }, []);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const selectedCount = multiple
        ? ((field.value as ValueType[] | undefined)?.length ?? 0)
        : undefined;

      // force the label to be shrunk if not empty
      if (multiple && label && !placeholder && selectedCount) {
        (params.InputLabelProps as InputLabelProps).shrink = true;
      }

      if (isUpdating) {
        params.InputProps.endAdornment = (
          <>
            <InputAdornment position="end">
              <CircularProgress size={16} color="inherit" />
            </InputAdornment>
            {params.InputProps.endAdornment}
          </>
        );
      }

      // InputProps appear to be provided by `params` which we don't have control over
      if (slotProps?.input) {
        params.InputProps = mergeSlotProps(
          slotProps.input ?? {},
          params.InputProps ?? {},
        );
      }

      if (!multiple) {
        params.inputProps = mergeSlotProps(
          {
            style: {
              width: Math.ceil(
                getTextSize(
                  getOptionLabel((field.value ?? "") as ValueType),
                  "14px Barlow, sans-serif",
                ).width + 16, // 16px fudge factor because the text sizing function isn't perfect ¯\_(ツ)_/¯
              ),
            },
          },
          params.inputProps ?? {},
        );
      }

      return (
        <TextField
          color={props.color}
          {...params}
          autoFocus={autoFocus}
          error={
            meta.touched
              ? Array.isArray(meta.error)
                ? (meta.error.find(Boolean) as string | undefined)
                : meta.error
              : undefined
          }
          slotProps={{
            htmlInput: params.inputProps,
            input: params.InputProps,
            inputLabel: params.InputLabelProps,
          }}
          label={label}
          onKeyDown={onKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          placeholder={
            placeholder ??
            // If we aren't using tags and are using multiselect, provide a
            // default Placeholder of `N Selected` or `None`.
            (noTags && multiple
              ? formatMessage(
                  { id: "components.combobox.{count} Selected" },
                  {
                    count: selectedCount,
                  },
                )
              : undefined)
          }
          required={required}
          tooltipContent={
            multiple ? null : getOptionLabel((field.value ?? "") as ValueType)
          }
          tooltipOpen={!dropdownOpen && tooltipOpen}
          tooltipPlacement={tooltipPlacement}
        />
      );
    },
    [
      autoFocus,
      dropdownOpen,
      field.value,
      formatMessage,
      getOptionLabel,
      label,
      isUpdating,
      meta.error,
      meta.touched,
      multiple,
      noTags,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      placeholder,
      props.color,
      required,
      slotProps?.input,
      tooltipOpen,
      tooltipPlacement,
    ],
  );

  const slotPropsMod = useMemo<
    AutocompleteProps<
      ValueType,
      Multiple,
      DisableClearable,
      FreeSolo
    >["slotProps"]
  >(() => {
    const popperProps = slotProps?.popper as PopperProps | undefined;
    return {
      ...slotProps,
      popper: mergeSlotProps(popperProps, {
        placement: "bottom-start",
        style: {
          // Allow the dropdown area to take up as much width as needed as
          // opposed to being restricted to the width of the Autocomplete widget
          width: "fit-content",
        },
      }),
    };
  }, [slotProps]);

  // Adds default `groupBy` logic if a `groupBy` prop is not provided and options
  // have `group` attributes.
  const groupBy = useMemo(
    () =>
      groupByProp ??
      (optionsLookup.values().some((option) => option?.group)
        ? (value: string) => optionsLookup.get(value as ValueType)?.group ?? ""
        : undefined),
    [groupByProp, optionsLookup],
  );

  return (
    <Autocomplete<ValueType, Multiple, DisableClearable, FreeSolo>
      {...(props as any)}
      autoHighlight={autoHighlight ?? freeSolo}
      slotProps={slotPropsMod}
      multiple={multiple}
      disabled={disabled}
      disableCloseOnSelect={disableCloseOnSelect || multiple}
      filterOptions={filterOptions}
      freeSolo={freeSolo}
      options={values}
      getOptionDisabled={
        readonly ? ALL_OPTIONS_DISABLED : props.getOptionDisabled
      }
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      renderOption={checklist ? renderCheckboxOption : renderOption}
      // MUI Autocomplete considers anything other than null (or an empty array)
      // as being dirty
      // This means that an empty string results in displaying the clear icon
      value={field.value || (multiple ? EMPTY_ARRAY : null)}
      onBlur={onBlurCallback}
      // The value argument to onChange is the new value for the field - in the
      // case of multiselect, value is an array. If we end up with SELECT_ALL_SYMBOL
      // in the new value array, we know that the user has just clicked Select All.
      onChange={onChangeCallback}
      // inputValue represents the currently typed state (and generally should be uncontrolled)
      // however, in order to support imperative handles, we need to be able to edit it
      inputValue={inputValue}
      onClose={onClose}
      onInputChange={onInputChange}
      onOpen={onOpen}
      open={dropdownOpen}
      renderInput={renderInput}
      renderTags={noTags ? nullRender : renderTags}
      slots={COMBOBOX_SLOTS}
    />
  );
}

export type ComboboxProps<
  Shape extends FormikValues,
  ValueType,
  Multiple extends Toggle,
  DisableClearable extends Toggle,
  FreeSolo extends Toggle,
> = ComboboxImplProps<
  Shape,
  ValueType,
  Multiple,
  DisableClearable,
  FreeSolo
> & {
  ref?: ComboboxRef<ValueType, Multiple, DisableClearable, FreeSolo>;
};

export const Combobox = forwardRef(ComboboxImpl) as <
  Shape extends FormikValues = FormikValues,
  ValueType = string,
>(
  props:
    | ComboboxProps<Shape, ValueType, Off, Off, Off>
    | ComboboxProps<Shape, ValueType, Off, Off, On>
    | ComboboxProps<Shape, ValueType, Off, On, Off>
    | ComboboxProps<Shape, ValueType, Off, On, On>
    | ComboboxProps<Shape, ValueType, On, Off, Off>
    | ComboboxProps<Shape, ValueType, On, On, Off>
    // Allow freeSolo and multiple
    | ComboboxProps<Shape, ValueType, On, Off, On>
    | ComboboxProps<Shape, ValueType, On, On, On>,
) => ReturnType<typeof ComboboxImpl>;
